import { Component, forwardRef, Input, OnDestroy, OnInit } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'casa-doc-input-date',
  templateUrl: './input-date.component.html',
  styleUrls: ['./input-date.component.scss'],
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputDateComponent),
      multi: true,
    },
  ],
})
export class InputDateComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() label = '';
  @Input() includeTime = false;
  @Input() required = false;
  @Input() disabled = false;
  @Input() hint = '';

  dateControl = new FormControl<Date | null>(null);
  private destroy$ = new Subject<void>();
  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};
  private previousDate: Date | null = null; // Mantiene la data precedente

  ngOnInit(): void {
    this.dateControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((date) => {
      if (date) {
        // Se includeTime è true e abbiamo una data precedente,
        // preserva l'ora della data precedente quando cambia solo il giorno
        if (this.includeTime && this.previousDate) {
          const newDate = new Date(date);
          const prevDate = new Date(this.previousDate);

          // Verifica se è cambiato solo il giorno (non l'ora)
          // Se l'ora della nuova data è 00:00:00, significa che è stata cambiata solo la data
          if (newDate.getHours() === 0 && newDate.getMinutes() === 0 && newDate.getSeconds() === 0) {
            // Preserva l'ora della data precedente
            newDate.setHours(prevDate.getHours());
            newDate.setMinutes(prevDate.getMinutes());
            newDate.setSeconds(prevDate.getSeconds());
            newDate.setMilliseconds(prevDate.getMilliseconds());

            // Aggiorna il controllo senza emettere evento per evitare loop
            this.dateControl.setValue(newDate, { emitEvent: false });
            this.previousDate = newDate;
            const formattedValue = this.formatDate(newDate);
            this.onChange(formattedValue);
            return;
          }
        }
        this.previousDate = date;
        const formattedValue = this.formatDate(date);
        this.onChange(formattedValue);
      } else {
        this.previousDate = null;
        this.onChange(null);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  writeValue(value: string | Date | null): void {
    if (value) {
      const date = typeof value === 'string' ? new Date(value) : value;
      this.previousDate = date;
      this.dateControl.setValue(date, { emitEvent: false });
    } else {
      this.previousDate = null;
      this.dateControl.setValue(null, { emitEvent: false });
    }
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.dateControl.disable();
    } else {
      this.dateControl.enable();
    }
  }

  onBlur(): void {
    this.onTouched();
  }

  private formatDate(date: Date): string {
    if (!this.includeTime) {
      // Format as YYYY-MM-DD
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } else {
      return date.toISOString();
    }
  }
}
